// The patient portal once signed in (docs/plans/phase3-portal-feedback-kiswahili.md §4.2, §7.1):
// whose records (the holder or a linked child), then appointments, bills, lab results,
// allergies and feedback. What reaches this page is already filtered by the server's exposure
// rules; the page only shows it, with loading, empty, error and offline states for each part.
import { formatEatDate, formatEatTime } from '@wodi/domain';
import type {
  AllergySummaryView,
  PortalAppointmentView,
  PortalBillView,
  PortalLabReportView,
  PortalMeView,
  SubmitFeedbackBody,
} from '@wodi/shared';
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { getRouteApi, useNavigate } from '@tanstack/react-router';
import {
  Button,
  Dialog,
  DialogActions,
  LoadingBlock,
  Notice,
  SegmentedControl,
  TextArea,
} from '@wodi/ui';
import { useCallback, useEffect, useState, type ReactNode } from 'react';

import { ApiError } from '../api/client.ts';
import {
  cancelPortalAppointment,
  portalAllergiesQuery,
  portalAppointmentsQuery,
  portalBillsQuery,
  portalFeedbackQuery,
  portalKeys,
  portalMeQuery,
  portalResultsQuery,
  portalSignOut,
  setPortalLanguage,
  submitPortalFeedback,
} from '../api/portal.ts';
import { money } from '../billing/format.ts';
import { useIdle } from '../hooks/use-idle.ts';
import { useOnline } from '../hooks/use-online.ts';
import { errorText, useI18n } from '../i18n/i18n.tsx';
import type { MessageKey } from '../i18n/messages/en.ts';
import { toasts } from '../toasts.ts';
import { FeedbackForm, RATING_KEYS } from './feedback-form.tsx';
import { PortalFrame } from './frame.tsx';
import styles from './portal.module.css';

const route = getRouteApi('/portal/$code/home');

const dateOf = (iso: string) => formatEatDate(new Date(iso));
const dayOf = (isoDay: string) => formatEatDate(new Date(`${isoDay}T09:00:00Z`));

/** A card with its own loading, error and offline states. */
function Card<T>({
  title,
  query,
  children,
}: {
  title: string;
  query: UseQueryResult<T>;
  children: (data: T) => ReactNode;
}) {
  const { t, language } = useI18n();
  const online = useOnline();
  const id = `portal-${title.replace(/\W+/g, '-').toLowerCase()}`;
  return (
    <section className={styles.card} aria-labelledby={id}>
      <h2 id={id} className={styles.cardTitle}>
        {title}
      </h2>
      {query.isPending ? <LoadingBlock label={t('common.loading')} rows={2} /> : null}
      {query.isError ? (
        <Notice
          tone="error"
          action={
            <Button dense isDisabled={!online} onPress={() => void query.refetch()}>
              {t('common.tryAgain')}
            </Button>
          }
        >
          {online ? errorText(t, language, query.error) : t('errors.offline')}
        </Notice>
      ) : null}
      {query.data === undefined ? null : children(query.data)}
    </section>
  );
}

function Appointments({ patientId }: { patientId: string }) {
  const { t, language } = useI18n();
  const queryClient = useQueryClient();
  const query = useQuery(portalAppointmentsQuery(patientId));
  const [cancelling, setCancelling] = useState<PortalAppointmentView | null>(null);
  const [reason, setReason] = useState('');
  const cancel = useMutation({
    mutationFn: (a: PortalAppointmentView) => cancelPortalAppointment(patientId, a.id, reason),
    onSuccess: async () => {
      setCancelling(null);
      setReason('');
      toasts.show({ title: t('portal.appointments.cancelled') });
      await queryClient.invalidateQueries({
        queryKey: portalKeys.section(patientId, 'appointments'),
      });
    },
  });
  return (
    <Card title={t('portal.appointments.title')} query={query}>
      {(data) =>
        data.appointments.length === 0 ? (
          <p className={styles.empty}>{t('portal.appointments.empty')}</p>
        ) : (
          <>
            <ul className={styles.list}>
              {data.appointments.map((a) => (
                <li key={a.id} className={styles.item}>
                  <div className={styles.itemHead}>
                    <span>{`${dateOf(a.startsAt)}, ${formatEatTime(new Date(a.startsAt))}`}</span>
                    <span className={styles.meta}>
                      {t(`portal.appointments.kind.${a.kind}` as MessageKey)}
                    </span>
                  </div>
                  <span>{`${a.clinicName}, ${a.facilityName}`}</span>
                  {a.practitionerName === null ? null : (
                    <span className={styles.meta}>
                      {t('portal.appointments.with', { name: a.practitionerName })}
                    </span>
                  )}
                  <div>
                    <Button
                      dense
                      onPress={() => {
                        cancel.reset();
                        setCancelling(a);
                      }}
                    >
                      {t('portal.appointments.cancel')}
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
            <Dialog
              title={t('portal.appointments.cancelTitle')}
              isOpen={cancelling !== null}
              onOpenChange={(open) => {
                if (!open) setCancelling(null);
              }}
            >
              <p>{t('portal.appointments.cancelBody')}</p>
              {cancel.error === null ? null : (
                <Notice tone="error">{errorText(t, language, cancel.error)}</Notice>
              )}
              <TextArea
                label={t('portal.appointments.cancelReason')}
                rows={2}
                maxLength={200}
                value={reason}
                onChange={setReason}
              />
              <DialogActions>
                <Button
                  onPress={() => {
                    setCancelling(null);
                  }}
                >
                  {t('portal.appointments.keep')}
                </Button>
                <Button
                  variant="destructive"
                  isPending={cancel.isPending}
                  onPress={() => {
                    if (cancelling !== null) cancel.mutate(cancelling);
                  }}
                >
                  {t('portal.appointments.cancel')}
                </Button>
              </DialogActions>
            </Dialog>
          </>
        )
      }
    </Card>
  );
}

/** A sentence with an identifier in the identifier font, wherever the language puts it. */
function WithIdentifier({
  template,
  name,
  value,
}: {
  template: string;
  name: string;
  value: string;
}) {
  const [before = '', after = ''] = template.split(`{${name}}`);
  return (
    <span>
      {before}
      <span className={styles.identifier}>{value}</span>
      {after}
    </span>
  );
}

function Bill({ bill }: { bill: PortalBillView }) {
  const { t } = useI18n();
  return (
    <li className={styles.item}>
      <div className={styles.itemHead}>
        <WithIdentifier template={t('portal.bills.visit')} name="number" value={bill.visitNumber} />
        <span className={styles.meta}>{dateOf(bill.startedAt)}</span>
      </div>
      <span className={styles.meta}>
        {`${bill.facilityName}. ${t('portal.bills.payer', { name: bill.payerName })}`}
      </span>
      <ul className={styles.lines} aria-label={t('portal.bills.item')}>
        {bill.lines.map((line, index) => (
          <li key={index} className={styles.line}>
            <span className={line.cancelled ? styles.cancelled : undefined}>
              {line.cancelled
                ? `${line.itemName} (${t('portal.bills.cancelledLine')})`
                : line.quantity === 1
                  ? line.itemName
                  : `${line.itemName} x ${String(line.quantity)}`}
            </span>
            <span className={styles.amount}>{money(line.patientAmount)}</span>
          </li>
        ))}
      </ul>
      <dl className={styles.totals}>
        <dt>{t('portal.bills.due')}</dt>
        <dd>{money(bill.due)}</dd>
        <dt>{t('portal.bills.paid')}</dt>
        <dd>{money(bill.paid)}</dd>
        <dt>{t('portal.bills.owed')}</dt>
        <dd>
          <strong>{money(bill.owed)}</strong>
        </dd>
      </dl>
      {bill.unpricedCount > 0 ? <p className={styles.meta}>{t('portal.bills.unpriced')}</p> : null}
      {bill.receipts.length === 0 ? null : (
        <ul className={styles.lines} aria-label={t('portal.bills.receipts')}>
          {bill.receipts.map((r, index) => (
            <li key={index} className={styles.line}>
              <span>
                {r.number === null
                  ? dateOf(r.receivedAt)
                  : t('portal.bills.receipt', { number: r.number })}
              </span>
              <span className={styles.amount}>{money(r.amount)}</span>
            </li>
          ))}
        </ul>
      )}
      {BigInt(bill.owed.cents) > 0n ? (
        <Notice tone="info">
          {bill.payment === null
            ? t('portal.bills.payAtFacility')
            : t('portal.bills.howToPay', {
                paybill: bill.payment.paybillNumber,
                account: bill.payment.accountNumber,
              })}
        </Notice>
      ) : null}
    </li>
  );
}

function Bills({ patientId }: { patientId: string }) {
  const { t } = useI18n();
  const query = useQuery(portalBillsQuery(patientId));
  return (
    <Card title={t('portal.bills.title')} query={query}>
      {(data) => (
        <>
          {data.bills.length === 0 ? (
            <p className={styles.empty}>{t('portal.bills.empty')}</p>
          ) : (
            <>
              <p>
                <strong>
                  {BigInt(data.totalOwed.cents) > 0n
                    ? t('portal.bills.totalOwed', { amount: money(data.totalOwed) })
                    : t('portal.bills.nothingOwed')}
                </strong>
              </p>
              <ul className={styles.list}>
                {data.bills.map((b) => (
                  <Bill key={b.visitId} bill={b} />
                ))}
              </ul>
            </>
          )}
          <p className={styles.meta}>{t('portal.bills.onlyShown')}</p>
          <p className={styles.meta}>{t('portal.bills.notProcessor')}</p>
        </>
      )}
    </Card>
  );
}

function Report({ report }: { report: PortalLabReportView }) {
  const { t } = useI18n();
  return (
    <li className={styles.item}>
      <div className={styles.itemHead}>
        <span>{report.testName}</span>
        <span className={styles.meta}>
          {t('portal.results.released', { date: dateOf(report.releasedAt) })}
        </span>
      </div>
      <table className={styles.results}>
        <thead>
          <tr>
            <th scope="col">{t('portal.results.test')}</th>
            <th scope="col">{t('portal.results.result')}</th>
            <th scope="col">{t('portal.results.range')}</th>
          </tr>
        </thead>
        <tbody>
          {report.results.map((r, index) => {
            const flag = t(`portal.results.flag.${r.interpretation}` as MessageKey);
            return (
              <tr key={index}>
                <td>{r.analyteName}</td>
                <td className={styles.value}>
                  {r.withheld ? (
                    t('portal.results.withheld')
                  ) : (
                    <>
                      {r.unit === null ? r.display : `${r.display} ${r.unit}`}
                      {flag === '' ? null : <span className={styles.flag}>{` ${flag}`}</span>}
                    </>
                  )}
                </td>
                <td>{r.range ?? ''}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {report.culture === null ? null : (
        <p>
          <strong>{`${t('portal.results.culture')}: `}</strong>
          {report.culture.outcome === 'growth'
            ? t('portal.results.culture.growth', { organisms: report.culture.organisms.join(', ') })
            : t(`portal.results.culture.${report.culture.outcome}`)}
          {report.culture.outcome === 'growth' ? (
            <span className={styles.meta}>{` ${t('portal.results.culture.sensitivities')}`}</span>
          ) : null}
        </p>
      )}
    </li>
  );
}

function Results({ patientId }: { patientId: string }) {
  const { t } = useI18n();
  const query = useQuery(portalResultsQuery(patientId));
  return (
    <Card title={t('portal.results.title')} query={query}>
      {(data) => (
        <>
          <p className={styles.meta}>{t('portal.results.whatIsShown')}</p>
          {data.reports.length === 0 ? (
            <p className={styles.empty}>{t('portal.results.empty')}</p>
          ) : (
            <ul className={styles.list}>
              {data.reports.map((r) => (
                <Report key={r.id} report={r} />
              ))}
            </ul>
          )}
        </>
      )}
    </Card>
  );
}

/** Always explicit (CLAUDE.md): a list, "No known allergies" or "Allergies not recorded". */
export function AllergyStatement({ allergies }: { allergies: AllergySummaryView }) {
  const { t } = useI18n();
  if (allergies.state === 'no_known_allergies') return <p>{t('portal.allergies.none')}</p>;
  if (allergies.state === 'not_recorded') {
    return (
      <>
        <p>
          <strong>{t('portal.allergies.notRecorded')}</strong>
        </p>
        <p className={styles.meta}>{t('portal.allergies.notRecordedHelp')}</p>
      </>
    );
  }
  return (
    <ul className={styles.allergyList}>
      {allergies.allergies.map((a) => (
        <li key={a.id}>
          <span className={styles.allergyName}>{a.substance}</span>
          {a.criticality === 'high' ? ` (${t('portal.allergies.high')})` : null}
          {a.verificationStatus === 'unconfirmed' ? `. ${t('portal.allergies.unconfirmed')}` : null}
          {a.reaction === null ? null : (
            <div className={styles.meta}>
              {t('portal.allergies.reaction', { reaction: a.reaction })}
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}

function Allergies({ patientId }: { patientId: string }) {
  const { t } = useI18n();
  const query = useQuery(portalAllergiesQuery(patientId));
  return (
    <Card title={t('portal.allergies.title')} query={query}>
      {(data) => <AllergyStatement allergies={data.allergies} />}
    </Card>
  );
}

function Feedback({ patientId }: { patientId: string }) {
  const { t, language } = useI18n();
  const queryClient = useQueryClient();
  const query = useQuery(portalFeedbackQuery(patientId));
  const [rating, setRating] = useState<string | null>(null);
  const submit = useMutation({
    mutationFn: (input: { visitId: string; body: SubmitFeedbackBody }) =>
      submitPortalFeedback(patientId, input.visitId, input.body),
    onSuccess: async () => {
      setRating(null);
      toasts.show({ title: t('feedback.thanks') });
      await queryClient.invalidateQueries({ queryKey: portalKeys.section(patientId, 'feedback') });
    },
  });
  return (
    <Card title={t('portal.feedback.title')} query={query}>
      {(data) => (
        <>
          {data.waiting.length === 0 ? (
            <p className={styles.empty}>{t('portal.feedback.noneWaiting')}</p>
          ) : (
            <ul className={styles.list}>
              {data.waiting.map((w) => (
                <li key={w.visitId} className={styles.item}>
                  <span>
                    {t('portal.feedback.waiting', {
                      date: dayOf(w.visitDate),
                      facility: w.facilityName,
                    })}
                  </span>
                  {rating === w.visitId ? (
                    <FeedbackForm
                      isPending={submit.isPending}
                      error={submit.error === null ? null : errorText(t, language, submit.error)}
                      onSubmit={(body) => {
                        submit.mutate({ visitId: w.visitId, body });
                      }}
                    />
                  ) : (
                    <div>
                      <Button
                        dense
                        onPress={() => {
                          submit.reset();
                          setRating(w.visitId);
                        }}
                      >
                        {t('portal.feedback.rate')}
                      </Button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
          {data.given.length === 0 ? null : (
            <ul className={styles.list}>
              {data.given.map((g) => (
                <li key={g.id} className={styles.item}>
                  <span>
                    {t('portal.feedback.given', {
                      date: dayOf(g.visitDate),
                      rating: t(RATING_KEYS[g.rating] ?? 'feedback.rating.3'),
                    })}
                  </span>
                  {g.comment === null ? null : <span className={styles.meta}>{g.comment}</span>}
                  {g.reply === null ? null : (
                    <Notice tone="info">{t('portal.feedback.reply', { reply: g.reply })}</Notice>
                  )}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </Card>
  );
}

function WhoseRecords({
  me,
  patientId,
  onChange,
}: {
  me: PortalMeView;
  patientId: string;
  onChange: (id: string) => void;
}) {
  const { t } = useI18n();
  if (me.dependants.length === 0) return null;
  return (
    <SegmentedControl
      label={t('portal.whose')}
      options={[
        { id: me.self.patientId, label: t('portal.yourself') },
        ...me.dependants.map((d) => ({ id: d.patientId, label: d.givenName })),
      ]}
      value={patientId}
      onChange={onChange}
    />
  );
}

export function PortalHomePage() {
  const { code } = route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const online = useOnline();
  const { t, language, setLanguage } = useI18n();
  const me = useQuery(portalMeQuery);
  const [chosen, setChosen] = useState<string | null>(null);

  const leave = useCallback(
    (reason: 'expired' | 'signed_out') => {
      queryClient.removeQueries({ queryKey: ['portal'] });
      void navigate({ to: '/portal/$code', params: { code }, search: { reason } });
    },
    [queryClient, navigate, code],
  );
  useIdle(me.data?.idleMinutes ?? 15, () => {
    leave('expired');
  });

  // The account's language, once known, wins over this device's last choice.
  const accountLanguage = me.data?.language;
  useEffect(() => {
    if (accountLanguage !== undefined) setLanguage(accountLanguage);
  }, [accountLanguage, setLanguage]);

  const signedOut = me.error instanceof ApiError && me.error.status === 401;
  useEffect(() => {
    if (signedOut) leave('expired');
  }, [signedOut, leave]);

  const signOut = useMutation({
    mutationFn: portalSignOut,
    onSettled: () => {
      leave('signed_out');
    },
  });

  if (me.isPending || signedOut) {
    return (
      <PortalFrame organisationName={t('portal.title')}>
        <LoadingBlock label={t('common.loading')} rows={4} />
      </PortalFrame>
    );
  }
  if (me.isError) {
    return (
      <PortalFrame organisationName={t('portal.title')}>
        <Notice
          tone="error"
          action={
            <Button dense isDisabled={!online} onPress={() => void me.refetch()}>
              {t('common.tryAgain')}
            </Button>
          }
        >
          {online ? errorText(t, language, me.error) : t('errors.offline')}
        </Notice>
      </PortalFrame>
    );
  }

  const data = me.data;
  const people = [data.self, ...data.dependants];
  const patientId = people.some((p) => p.patientId === chosen)
    ? (chosen ?? '')
    : data.self.patientId;
  const person = people.find((p) => p.patientId === patientId) ?? data.self;

  return (
    <PortalFrame
      organisationName={data.organisationName}
      onLanguageChange={(next) => {
        void setPortalLanguage(next);
      }}
      actions={
        <Button
          dense
          isPending={signOut.isPending}
          onPress={() => {
            signOut.mutate();
          }}
        >
          {t('portal.signOut')}
        </Button>
      }
    >
      {online ? null : <Notice tone="info">{t('portal.offline')}</Notice>}
      <div>
        <h1 className={styles.title}>{person.givenName}</h1>
        <p className={styles.meta}>
          {`${t('portal.patientNumber')}: `}
          <span className={styles.identifier}>{person.patientNumber}</span>
        </p>
      </div>
      <WhoseRecords me={data} patientId={patientId} onChange={setChosen} />
      <Allergies key={`a-${patientId}`} patientId={patientId} />
      <Appointments key={`p-${patientId}`} patientId={patientId} />
      <Bills key={`b-${patientId}`} patientId={patientId} />
      <Results key={`r-${patientId}`} patientId={patientId} />
      <Feedback key={`f-${patientId}`} patientId={patientId} />
    </PortalFrame>
  );
}
